/* eslint-disable array-callback-return */
import { createSlice, current } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import _, { cloneDeep } from "lodash";
import { calculateAdjustments, calculateAll } from "utils/calculations";

function parseDocumentProductService(prodData) {
  return {
    ...prodData,
    quantity: parseFloat(prodData.quantity),
    sell: parseFloat(prodData.sell),
    cost: parseFloat(prodData.cost),
    total: parseFloat(prodData.total),
    discount: parseFloat(prodData.discount)
  };
}

function addManufacturer(productServices) {
  const manufacturerIds = [];
  productServices.map((pc) => {
    if (pc.manufacturer_id && !manufacturerIds.includes(pc.manufacturer_id, 0)) {
      manufacturerIds.push(pc.manufacturer_id);
    }
  });
  return manufacturerIds;
}

function addLocation(productServices) {
  const locations = [];
  productServices.map((pc) => {
    if (pc.location && !locations.some((lc) => lc.code === pc.location)) {
      locations.push({ name: pc.location, code: pc.location });
    }
  });
  return locations;
}

function getProductServiceItemIds(layout) {
  const productServiceItemIds = [];
  layout.map((item) => {
    if (item?.typeId === "product_service") {
      productServiceItemIds.push(item.i);
    }
  });
  return productServiceItemIds;
}

function calculateDocumentTotalInLink(productServices) {
  const total = {
    taxableSubTotal: 0,
    nonTaxableSubTotal: 0
  };

  const newProductServices = productServices.map((pc) => {
    const newPC = { ...pc };
    const isOptionalChecked = pc.optional ? pc.optional_is_selected : true;
    if (newPC.taxable) {
      newPC.total = isOptionalChecked ? +newPC.sell * +newPC.quantity - +newPC.discount : 0;
      if (!newPC.recurrence_include_in_total || isOptionalChecked) {
        total.taxableSubTotal += Number(newPC.total);
      }
      return newPC;
    }

    newPC.total = isOptionalChecked ? +newPC.sell * +newPC.quantity - +newPC.discount : 0;
    if (!newPC.recurrence_include_in_total || isOptionalChecked) {
      total.nonTaxableSubTotal += Number(newPC.total);
    }
    return newPC;
  });

  return { newProductServices, total };
}

const ZERO = 0;
const _documentTotalConst = {
  pre_adjusted_subtotal: ZERO,
  sub_total: ZERO,
  one_time_subtotal: ZERO,
  monthly_subtotal: ZERO,
  bi_monthly_subtotal: ZERO,
  quarterly_subtotal: ZERO,
  semi_annual_subtotal: ZERO,
  annual_subtotal: ZERO,
  adjustment_amount: ZERO,
  sales_tax: ZERO,
  sales_tax_rate: ZERO,
  grand_total: ZERO,
  document_cost: ZERO,
  shipping: ZERO,
  shipping_tax: true
};
export const documentSettingsSlice = createSlice({
  name: "document",
  initialState: {
    undoManager: {},
    selectedDocumentId: null,
    externalIcons: [],
    isFetched: false,
    isLoading: false,
    columnNames: [],
    documents: [],
    documents_task: [],
    allDocuments: [],
    fetchedPages: [],
    count: 0,
    pagination: {
      skip: 0,
      limit: 10,
      sortInfo: {},
      groupBy: [],
      filterValue: []
    },
    mergeFields: {
      isLoading: false,
      isLoaded: false,
      allMergeFields: [],
      data: [],
      error: null
    },
    cardLabels: {},
    cardStructure: {},

    contactsSelectSearch: {
      isLoading: false,
      isLoaded: false,
      data: [],
      error: null
    },

    companiesSelectSearch: {
      isLoading: false,
      isLoaded: false,
      data: [],
      error: null
    },

    shipping: {
      isLoading: false,
      isLoaded: false,
      data: {},
      error: undefined
    },

    oldDocument: {
      item: {},
      head: {}
    },
    // single document
    document: {
      isFetchedHead: false,
      isFetchedItem: false,
      isLoadingHead: false,
      isLoading: false,
      isReadOnly: false,
      isCopy: false,
      data: {
        head: undefined,
        item: undefined
      },
      productServices: {
        isEdit: false,
        productServiceItemIds: [],
        list: [],
        _list: []
      },
      textElements: []
    },
    inventory: {
      isLoading: false,
      isFetched: false,
      list: []
    },
    productLookup: {
      loadingText: "",
      list: []
    },
    documentTotal: {
      taxableSubTotal: 0,
      nonTaxableSubTotal: 0,
      subTotal: 0,
      adjustedTaxableSubTotal: 0,
      adjustedNonTaxableSubTotal: 0,
      adjustedCost: 0,
      adjustedSell: 0,
      adjustedSubTotal: 0,
      nonAdjustedTaxableSubTotal: 0,
      nonAdjustedFixedSubTotal: 0,
      nonAdjustedRecurringSubTotal: 0,
      nonAdjustedSubTotal: 0,
      adjustedFixedSubTotal: 0,
      adjustedRecurringSubTotal: 0,
      adjustedRecurringSubTotalReference: 0,
      showOneTimeSubTotal: false
    },
    _documentTotal: _documentTotalConst,

    totalCost: 0,

    tenantSettings: {
      isFetched: false,
      isLoading: false,
      data: undefined
    },

    documentNotes: {
      isFetched: false,
      data: {},
      columnNames: [],
      pagination: {
        skip: 0,
        limit: 10,
        sortInfo: true,
        groupBy: [],
        filterValue: []
      }
    },

    documentTasks: {
      isFetched: false,
      data: {},
      isLoading: false,
      columnNames: [],
      pagination: {
        skip: 0,
        limit: 10,
        sortInfo: true,
        groupBy: [],
        filterValue: []
      }
    },

    documentRevisions: {
      isFetched: false,
      data: {},
      columnNames: [],
      isLoading: false
    },

    selectedSignatures: [],

    status: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    type: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    template: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    documentLocations: [],
    documentManufacturerIds: [],
    allDocumentAttributes: {
      isFetched: false
    },
    adjustment_source: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    adjustment_destination: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    payment_term: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        isLoading: false,
        list: []
      }
    },
    documentProductServiceSearch: {
      isFetched: false,
      isLoading: false,
      isLoadingMore: false,
      pagination: {
        skip: 0,
        limit: 0
      },
      list: [],
      totalCount: 0,
      columnNames: [],
      inStock: false
    },
    outsourceProductService: {
      isFetched: false,
      isLoading: false,
      product: undefined
    },
    isLayoutChange: null,

    draftData: {}
  },
  reducers: {
    onDocumentUndoManager(state, { payload }) {
      state.undoManager[payload.name] = payload;
    },
    onGetExternalIcons(state, { payload }) {
      state.externalIcons = payload;
    },
    onGetContactsSelectSearch(state) {
      state.contactsSelectSearch.isLoading = true;
    },

    onGetContactsSelectSearchSuccess(state, { payload }) {
      state.contactsSelectSearch.isLoading = false;
      state.contactsSelectSearch.isLoaded = true;
      state.contactsSelectSearch.data = payload;
    },

    onGetContactsSelectSearchFailure(state, { payload }) {
      state.contactsSelectSearch.isLoading = false;
      state.contactsSelectSearch.isLoaded = true;
      state.contactsSelectSearch.data = [];
      state.contactsSelectSearch.error = payload;
    },

    onGetCompaniesSelectSearch(state) {
      state.companiesSelectSearch.isLoading = true;
    },

    onGetCompaniesSelectSearchSuccess(state, { payload }) {
      state.companiesSelectSearch.isLoading = false;
      state.companiesSelectSearch.isLoaded = true;
      state.companiesSelectSearch.data = payload;
    },

    onGetCompaniesSelectSearchFailure(state, { payload }) {
      state.companiesSelectSearch.isLoading = false;
      state.companiesSelectSearch.isLoaded = true;
      state.companiesSelectSearch.data = [];
      state.companiesSelectSearch.error = payload;
    },

    onReceiveLookupNewMessage(state, { payload }) {
      state.productLookup.loadingText = payload;
    },
    onReceiveLookupNewData(state, { payload }) {
      const currentList = cloneDeep(state.productLookup.list);
      state.productLookup.list = [...currentList, payload.data];
      state.productLookup.loadingText = "";
    },
    resetLookupData(state) {
      state.productLookup.list = [];
      state.productLookup.loadingText = "";
    },
    onLoadMergeFields(state) {
      state.mergeFields.isLoading = true;
    },
    onSuccessMergeFields(state, { payload }) {
      const currentAllMergeFields = cloneDeep(state.mergeFields.allMergeFields);
      payload.items.forEach((item) => {
        const token = item.document_token || item.contact_token;
        const existingMF = currentAllMergeFields.find((current) => current.contact_token === token);

        if (existingMF) {
          Object.assign(existingMF, item);
        } else {
          currentAllMergeFields.push(item);
        }
      });

      state.mergeFields.data = payload.items;
      state.mergeFields.allMergeFields = currentAllMergeFields;
      state.mergeFields.isLoading = false;
      state.mergeFields.isLoaded = true;
    },
    onFailedMergeFields(state, { payload }) {
      state.mergeFields.error = payload;
      state.mergeFields.isLoading = false;
      state.mergeFields.isLoaded = true;
    },
    resetMergeFields(state) {
      state.mergeFields.allMergeFields = [];
      state.mergeFields.data = [];
    },

    onPdfImport: (state, { payload }) => {
      const currentList = _.cloneDeep(state.document.productServices._list);
      const docHead = _.cloneDeep(state.document.data.head);
      const newProductServices = payload.data.product?.map((p) => ({
        ...payload.payload.newPs,
        vendor_id: null,
        vendor_name: null,
        ...p,
        cost: p.cost ? Number(p.cost) : 0,
        document_item_product_service_id: uuidv4(),
        document_item_id: payload.payload.productServiceId,
        document_item_product_service_parent_id: null,
        tenant_id: docHead.tenant_id
      }));
      state.document.productServices._list = [...currentList, ...newProductServices];
      docHead.shipping = (Number(payload.data.shipping) || 0) + (Number(docHead.shipping) || 0) || null;
    },
    onShippingEditCall: (state) => {
      state.shipping.isLoading = true;
    },
    onShippingEditSuccess: (state, { payload }) => {
      state.shipping.isLoading = false;
      state.shipping.isLoaded = true;
      state.shipping.data = payload;
      state.document.data.head = { ...state.document.data.head, ...payload };
    },
    onShippingEditFailure: (state, { payload }) => {
      state.shipping.isLoading = false;
      state.shipping.isLoaded = true;
      state.shipping.error = payload;
    },
    onLayoutChange: (state, { payload: { payload } }) => {
      state.isLayoutChange = payload;
    },
    onCallOutsourceProductService: (state) => {
      state.outsourceProductService.isFetched = false;
      state.outsourceProductService.isLoading = true;
    },
    onSuccessOutsourceProductService: (state, { payload }) => {
      state.outsourceProductService.isFetched = true;
      state.outsourceProductService.isLoading = false;
      state.outsourceProductService.product = payload;
    },
    onFailedOutsourceProductService: (state) => {
      state.documentProductServiceSearch.isLoading = false;
      state.documentProductServiceSearch.isFetched = true;
    },
    onCallDocumentProductServicesSearch: (state, { payload }) => {
      state.documentProductServiceSearch.pagination = {
        skip: payload?.skip || 0,
        limit: payload?.limit || 10
      };
      state.documentProductServiceSearch.isLoading = true;
      state.documentProductServiceSearch.isFetched = false;
      state.documentProductServiceSearch.columnNames = [];
      state.documentProductServiceSearch.count = 0;
      state.documentProductServiceSearch.list = [];
    },
    onCallDocumentProductServicesSearchPromise: (state, { payload }) => {
      state.documentProductServiceSearch.pagination = {
        skip: payload?.skip || 0,
        limit: payload?.limit || 10
      };
      // When loading more (skip > 0), show bottom loader
      if (payload?.skip > 0) {
        state.documentProductServiceSearch.isLoadingMore = true;
      }
    },
    onSuccessDocumentProductServicesSearchPromise: (state, { payload }) => {
      state.documentProductServiceSearch.isLoadingMore = false;
      if (state.documentProductServiceSearch.inStock !== payload.in_stock) {
        state.documentProductServiceSearch.list = [...payload.data.tableData];
        state.documentProductServiceSearch.totalCount = parseInt(payload.data.total_count, 10) || 0;
        state.documentProductServiceSearch.columnNames = payload.data.columnNames;
        state.documentProductServiceSearch.inStock = payload.in_stock;
      } else {
        state.documentProductServiceSearch.list = [
          ...state.documentProductServiceSearch.list,
          ...payload.data.tableData
        ];
        // Keep totalCount from API only when it represents full result set (e.g. first page or same as before).
        // On load-more some APIs return page count; avoid overwriting with that.
        const apiTotal = parseInt(payload.data.total_count, 10) || 0;
        if (apiTotal >= state.documentProductServiceSearch.list.length) {
          state.documentProductServiceSearch.totalCount = apiTotal;
        }
        state.documentProductServiceSearch.columnNames = payload.data.columnNames;
      }
    },
    onFailedDocumentProductServicesSearch: (state) => {
      state.documentProductServiceSearch.isLoading = false;
      state.documentProductServiceSearch.isLoadingMore = false;
      state.documentProductServiceSearch.isFetched = true;
      state.documentProductServiceSearch.totalCount = 0;
      state.documentProductServiceSearch.list = [];
      state.documentProductServiceSearch.columnNames = [];
    },
    onFailedDocumentProductServicesSearchPromise: (state) => {
      state.documentProductServiceSearch.isLoadingMore = false;
    },
    onCallDocumentProductServiceCheckInventory: (state) => {
      state.inventory.isLoading = true;
      // state.inventory.list =
    },
    onSuccessDocumentProductServiceCheckInventory: (state, { payload }) => {
      state.inventory.isLoading = false;
      state.inventory.list = payload;
      state.inventory.isFetched = true;
    },
    onFailedDocumentProductServiceCheckInventory: (state) => {
      state.inventory.isLoading = false;
      state.inventory.isFetched = true;
      // state.documentProductServiceSearch.inventory.list =
    },
    onResetDocumentProductServiceInventory: (state) => {
      state.inventory.isLoading = false;
      state.inventory.list = [];
      state.inventory.isFetched = false;
    },
    resetDocumentProductServicesSearch: (state) => {
      state.documentProductServiceSearch.isFetched = false;
      state.documentProductServiceSearch.columnNames = [];
      state.documentProductServiceSearch.isLoading = false;
      state.documentProductServiceSearch.isLoadingMore = false;
      state.documentProductServiceSearch.count = 0;
      state.documentProductServiceSearch.list = [];
      state.documentProductServiceSearch.totalCount = 0;
    },
    onSuccessDocumentProductServicesSearch: (state, { payload }) => {
      state.documentProductServiceSearch.isLoading = false;
      state.documentProductServiceSearch.isFetched = true;
      state.documentProductServiceSearch.list = payload.tableData;
      state.documentProductServiceSearch.totalCount = parseInt(payload.total_count, 10) || 0;
      state.documentProductServiceSearch.columnNames = payload.columnNames;
    },
    onClearDocumentProductServicesSearch: (state) => {
      state.documentProductServiceSearch = {
        isFetched: false,
        isLoading: false,
        isLoadingMore: false,
        pagination: {
          skip: 0,
          limit: 0
        },
        list: [],
        totalCount: 0,
        columnNames: [],
        inStock: false
      };
    },
    // all documents
    onCallDocuments: (state, { payload }) => {
      if (!payload) {
        // initial call
        state.isFetched = false;
        state.isLoading = true;
        state.columnNames = [];
        state.count = 0;
      } else {
        // with pagination
        state.pagination = payload;
      }
      state.documents = [];
    },
    onSuccessDocuments: (state, { payload }) => {
      state.isFetched = true;
      state.isLoading = false;
      state.pagination = payload.paginationData;
      state.count = parseInt(payload?.total_count, 10);

      const currentPage = state?.pagination?.skip / state?.pagination.limit + 1;
      const currentPages = cloneDeep(state.fetchedPages);

      if (!currentPages?.find((item) => item === currentPage)) {
        state.fetchedPages = [...currentPages, currentPage];
        state.allDocuments = state.allDocuments.concat(payload.tableData);
      }

      if (payload.uiViewType === "card") {
        state.documents = payload.shouldClear ? payload.tableData : state.documents.concat(payload.tableData);
        state.pagination.skip = payload.shouldClear
          ? payload.paginationData.limit
          : payload.paginationData.skip + payload.paginationData.limit;
        state.cardLabels = payload.structure.card_label;
        state.cardStructure = payload.structure.card_structure;
      } else {
        state.documents = payload.tableData;
        state.columnNames = payload.columnNames;
      }
    },
    onSuccessDocumentsTask: (state, { payload }) => {
      state.isFetched = true;
      state.documents_task = payload;
    },
    onFailedDocumentsTask: (state) => {
      state.isFetched = true;
      state.documents_task = [];
    },
    onFailedDocuments: (state) => {
      state.isFetched = true;
      state.isLoading = false;
      state.documents = [];
      state.columnNames = [];
      state.count = 0;
    },

    switchDocumentsLoading: (state, { payload }) => {
      state.document.isLoading = payload !== undefined ? payload : !state.document.isLoading;
    },

    updateDocumentHead: (state, { payload }) => {
      const currentHead = cloneDeep(state.document.data.head);
      state.document.data = {
        item: state.document.data.item,
        head: {
          ...currentHead,
          ...payload
        }
      };
    },

    onSuccessDocumentHead: (state, { payload: { headData } }) => {
      state.document.isFetchedHead = true;
      state.document.data = {
        item: state.document.data.item,
        head: headData?.document
      };
      if (state.document?.productServices?._list?.length) {
        const _headData = state.document.data.head;
        const productServices = state.document.productServices._list;
        const calc = calculateAll(_headData, productServices, ["currency_id", "currency"]);
        state._documentTotal = calc;
      }
    },
    onSuccessDocumentHeadWithChangedFields: (state, { payload: { headData, fields } }) => {
      state.oldDocument.head = cloneDeep(state.document.data.head);
      state.document.isFetchedHead = true;
      state.document.data = {
        item: state.document.data.item,
        head: {
          ...headData?.document,
          ...fields,
          template_document_number: headData?.document.document_number,
          document_name: state.document.data?.head?.document_name || ""
        }
      };
    },

    revertDocumentItemsFromTemplate: (state) => {
      state.document.data.item = cloneDeep(state.oldDocument.item);
      state.document.data.head = cloneDeep(state.oldDocument.head);
      state.document.productServices._list = state.oldDocument.product_services;
    },

    onUpdateDocumentItemFromTemplate: (state, { payload }) => {
      const clonedDocument = cloneDeep(state.document);
      state.oldDocument.item = cloneDeep(state.document.data.item);
      state.oldDocument.product_services = clonedDocument.productServices?._list;
      const deletedItemIds = clonedDocument?.data.item?.layoutData?.layout?.map((item) => item.i);

      const deletedProducts = clonedDocument.productServices?._list.map(
        (item) => item.document_item_product_service_id
      );
      state.document.data = {
        item: {
          ...clonedDocument.data.item,
          layoutData: { ...payload.layoutData, deletedItemIds, deletedProducts },
          productServices: payload.productServices,
          sales_tax_rate: payload.sales_tax_rate,
          tableData: payload.tableData
        },
        head: state.document.data.head
      };

      const newProductServices = payload.productServices.map((dps) => parseDocumentProductService(dps));
      state.document.productServices.productServiceItemIds = getProductServiceItemIds(payload.layoutData.layout);
      if (payload.productServices.length) {
        state.documentLocations = addLocation(payload.productServices);
        state.documentManufacturerIds = addManufacturer(payload.productServices);
        const adjustedProductServices = calculateAdjustments(
          newProductServices,
          state.document.productServices.productServiceItemIds,
          payload.tableData ?? []
        );
        state.document.productServices._list = adjustedProductServices;
      }
    },

    onSuccessDocumentItem: (state, { payload: { itemData } }) => {
      if (itemData?.document_items?.[0]?.document_item_read_only_data) {
        state.document.isReadOnly = true;
      }

      state.document.isFetchedItem = true;
      state.document.data = {
        // head: state.document.data.head,
        ...state.document.data,
        item: itemData
          ? {
              layoutData: itemData.layoutData,
              sales_tax_rate: itemData.sales_tax_rate,
              documentId: itemData.documentId
            }
          : {}
      };
      const newProductServices = itemData.productServices.map((dps) => parseDocumentProductService(dps));
      state.document.productServices.productServiceItemIds = getProductServiceItemIds(itemData.layoutData.layout);
      if (itemData.productServices.length) {
        state.documentLocations = addLocation(itemData.productServices);
        state.documentManufacturerIds = addManufacturer(itemData.productServices);
        const adjustedProductServices = calculateAdjustments(
          newProductServices,
          state.document.productServices.productServiceItemIds,
          itemData.tableData ?? []
        );
        state.document.productServices._list = adjustedProductServices;
      }
      state.document.isLoading = false;
    },
    onFailedDocument: (state) => {
      state.document.isFetchedItem = true;
      state.document.isFetchedHead = true;
      state.document.isLoading = false;
      state.document.data = {
        head: undefined,
        item: undefined
      };
    },
    resetDocumentHead: (state) => {
      state.document.isFetchedHead = false;
      state.document.data = {
        head: undefined,
        item: state.document.data.item
      };
    },
    resetDocumentItem: (state) => {
      state.document.isReadOnly = false;
      state.document.isFetchedItem = false;
      state.document.isLoading = false;
      state.document.data = {
        head: state.document.data.head,
        item: undefined
      };
      state.document.productServices = {
        isEdit: false,
        productServiceItemIds: [],
        list: [],
        _list: []
      };
    },
    onCallDocumentNotes: (state, { payload }) => {
      state.documentNotes.isLoading = true;
      if (!payload?.isSocketUpdate) {
        state.documentNotes.isFetched = false;
      }
      if (payload?.paginationData) {
        state.documentNotes.pagination = payload.paginationData;
      }
      state.documentNotes.columnNames = [];
      state.documentNotes.data = [];
    },
    onSuccessDocumentNotes: (state, { payload }) => {
      state.documentNotes.isFetched = true;
      state.documentNotes.data = payload.tableData;
      state.documentNotes.columnNames = payload.columnNames;
      state.documentNotes.isLoading = false;
      state.documentNotes.pagination = payload.paginationData;
      state.documentNotes.count = parseInt(payload?.total_count, 10) || 0;
    },
    resetDocumentNotes: (state) => {
      state.documentNotes.isFetched = false;
      state.documentNotes.data = {};
      state.documentNotes.columnNames = [];
    },
    onAddNewNote: (state, { payload }) => {
      const currentNotes = cloneDeep(state.documentNotes.data);
      state.documentNotes.data = [...payload, ...currentNotes];
    },
    onFailedDocumentNotes: (state) => {
      state.documentNotes.isFetched = true;
      state.documentNotes.data = [];
      state.documentNotes.columnNames = [];
      state.documentNotes.isLoading = false;
    },
    onCallDocumentAddNote: (state) => {
      state.documentNotes.isLoading = true;
    },
    onCallDocumentTasks: (state, payload) => {
      if (!payload?.isSocketUpdate) {
        state.documentTasks.isFetched = false;
      }
      if (payload?.paginationData) {
        state.documentTasks.pagination = payload.paginationData;
      }
      state.documentTasks.isLoading = true;
      state.documentTasks.columnNames = [];
      state.documentTasks.data = [];
    },
    onSuccessDocumentTasks: (state, { payload }) => {
      state.documentTasks.isFetched = true;
      state.documentTasks.isLoading = false;
      state.documentTasks.data = payload.tableData;
      state.documentTasks.columnNames = payload.columnNames;
      state.documentTasks.pagination = payload.paginationData;
      state.documentTasks.count = parseInt(payload?.total_count, 10) || 0;
    },
    onFailedDocumentTasks: (state) => {
      state.documentTasks.isFetched = true;
      state.documentTasks.isLoading = false;
      state.documentTasks.data = [];
      state.documentTasks.columnNames = [];
    },
    resetDocumentTasks: (state) => {
      state.documentTasks.isFetched = false;
      state.documentTasks.data = {};
      state.documentTasks.columnNames = [];
    },
    onCallDocumentRevisions: (state, { payload }) => {
      if (!payload) {
        state.documentRevisions.isFetched = false;
      }
      const isSocketUpdate = payload === true || payload?.isSocketUpdate;
      if (!isSocketUpdate) {
        state.documentRevisions.isLoading = true;
        state.documentRevisions.columnNames = [];
        state.documentRevisions.data = [];
      }
    },
    onSuccessDocumentRevisions: (state, { payload }) => {
      state.documentRevisions.isFetched = true;
      state.documentRevisions.isLoading = false;
      state.documentRevisions.data = payload.tableData;
      state.documentRevisions.columnNames = payload.columnNames;
    },
    onFailedDocumentRevisions: (state) => {
      state.documentRevisions.isFetched = true;
      state.documentRevisions.data = [];
      state.documentRevisions.isLoading = false;
      state.documentRevisions.columnNames = [];
    },

    onSuccessDocumentAdd: (state, { payload }) => {
      if (payload) {
        const currentList = cloneDeep(state.documents);
        state.documents = [payload, ...currentList];
        state.count += 1;
      }
    },
    onSuccessDocumentEdit: (state, { payload: { data, documentData } }) => {
      if (data.document) {
        const currentList = cloneDeep(state.documents);
        state.documents = currentList.map((document) => {
          if (document.document_id === data.document.document_id) {
            return {
              ...document,
              ...data.document,
              Active: data?.document?.active
            };
          }
          return document;
        });
      }
      if (documentData) {
        state.document.data.head = {
          ...state.document.data.head,
          ...documentData,
          ...data.document,
          document_name: documentData.name
        };
      }
    },
    onSuccessDocumentApprove: (state, { payload }) => {
      if (payload) {
        state.document.data.head = {
          ...state.document.data.head,
          ...payload
        };
        state.document.isReadOnly = true;
      }
    },
    onSuccessDocumentsDelete: (state, { payload }) => {
      if (payload !== undefined) {
        state.documents = state.documents.filter((document) => !payload.includes(document.document_id));
        state.count -= 1;
      }
    },
    onSuccessDocumentRequestApproval: (state, { payload }) => {
      if (payload) {
        state.document.data.head = {
          ...state.document.data.head,
          approval_request: true
        };
      }
    },

    _onSuccessDocumentItemAdd: (state, { payload }) => {
      if (payload) {
        state.document.data.item = {
          layoutData: payload.layoutData,
          sales_tax_rate: payload.sales_tax_rate,
          documentId: payload.documentId
        };
        state.document.productServices.productServiceItemIds = getProductServiceItemIds(payload.layoutData.layout);
        state.document.productServices.isEdit = false;
        if (payload.productServices.length) {
          const locations = payload.productServices.reduce((locations, pc) => {
            if (pc.location && !locations.some((lc) => lc.code === pc.location)) {
              locations.push({
                name: pc.location,
                code: pc.location
              });
            }
            return locations;
          }, []);
          state.documentLocations = locations;
          const manufacturerIds = payload.productServices.reduce((manufacturerIds, pc) => {
            if (pc.manufacturer_id && !manufacturerIds.includes(pc.manufacturer_id, 0)) {
              manufacturerIds.push(pc.manufacturer_id);
            }
            return manufacturerIds;
          }, []);
          state.documentManufacturerIds = manufacturerIds;

          const calc = calculateAll(state.document.data.head, payload.productServices, ["currency_id", "currency"]);

          state._documentTotal = {
            ...calc
          };
          state.document.productServices._list = payload.productServices;
        } else {
          state.document.productServices._list = [];
        }
      }
    },
    onSuccessLocation: (state, { payload }) => {
      const { code, isDefault: isDefaultFromUser } =
        typeof payload === "object" ? payload : { code: payload, isDefault: false };
      const locations = state.documentLocations.map((item) => ({ ...item, isDefault: false }));
      if (!locations.some((lc) => lc.code === code)) {
        state.documentLocations = [...locations, { name: code, code, isDefault: isDefaultFromUser }];
      }
    },
    onSuccessDocumentItemEdit: (state, { payload }) => {
      if (payload) {
        state.document.data.item = payload.document_item;
      }
    },

    switchDocumentProductServiceLoading: (state, { payload }) => {
      state.document.productServices.isLoading =
        payload !== undefined ? payload : !state.document.productServices.isLoading;
    },

    onSuccessDocumentProductServiceAdd: (state, { payload }) => {
      if (payload) {
        const newState = [...state.document.productServices._list];
        newState.push(parseDocumentProductService(payload));
        state.document.productServices._list = newState;
      }
    },
    onSuccessEditProductServiceItems: (state, { payload }) => {
      // console.log('onSuccessEditProductServiceItems');
      const { id, type } = payload;
      switch (type) {
        case "duplicateItem":
        case "addItem": {
          state.document.productServices.productServiceItemIds = [
            ...state.document.productServices.productServiceItemIds,
            id
          ];
          break;
        }
        case "removeItem": {
          const newProductServiceItemIds = state.document.productServices.productServiceItemIds.filter(
            (itemID) => itemID !== id
          );
          state.document.productServices.productServiceItemIds = newProductServiceItemIds;
          break;
        }
        default:
          break;
      }
      const newProductServices = state.document.productServices._list.map((pc) => {
        delete pc.document_sell;
        return pc;
      });
      const calc = calculateAll(state.document.data.head, newProductServices, ["currency_id", "currency"]);
      state._documentTotal = {
        ...calc
      };
    },
    onSuccessDocumentProductServiceEdit: (state, { payload }) => {
      const { productServiceData, location, itemToDelete } = payload;

      if (itemToDelete) {
        const oldList = _.cloneDeep(state.document.productServices._list);
        const productServiceNewList = oldList.filter((pc) => pc.document_item_product_service_id !== itemToDelete);
        state.document.productServices.isEdit = true;
        const calc = calculateAll(state.document.data.head, productServiceNewList, ["currency_id", "currency"]);

        state.document.productServices._list = productServiceNewList;
        state._documentTotal = {
          ...calc
        };
        return;
      }

      const documentItemId = productServiceData?.[0]?.document_item_id;
      const oldList = cloneDeep(state.document.productServices._list);
      const productServiceOldList = oldList.filter((pc) => pc.document_item_id !== documentItemId);
      const itemsToAdd = (productServiceData || []).filter((item) => item.document_item_product_service_id);

      const productServiceNewList = [...productServiceOldList, ...itemsToAdd];

      const productServiceNewListUnique = productServiceNewList.reduce((acc, current) => {
        const foundItem = acc.find(
          (item) => item.document_item_product_service_id === current.document_item_product_service_id
        );
        if (!foundItem) {
          acc.push(current);
          return acc;
        }
        return acc;
      }, []);
      state.document.productServices.isEdit = true;
      const newList = _.cloneDeep(productServiceNewListUnique);
      const calc = calculateAll(state.document.data.head, newList, ["currency_id", "currency"]);

      state.document.productServices._list = newList;
      state._documentTotal = {
        ...calc
      };

      if (location) {
        const newDocumentLocation = state.documentLocations.map((lc) =>
          lc.code === location ? { ...lc, isDefault: true } : { ...lc, isDefault: false }
        );
        state.documentLocations = newDocumentLocation;
      }
    },
    onSuccessDocumentProductServicesDelete: (state, { payload }) => {
      if (payload !== undefined) {
        state.document.productServices._list = state.document.productServices._list.filter(
          (ps) => !payload.includes(ps.document_item_product_service_id)
        );
      }
    },

    onCallDocumentSettings: (state, { payload }) => {
      state[payload.key].isFetched = false;
      state[payload.key].list = [];
      state[payload.key].columnNames = [];
    },
    onSuccessDocumentSettings: (state, { payload }) => {
      state[payload.key].isFetched = true;
      state[payload.key].list = payload.data.tableData;
      state[payload.key].columnNames = payload.data.columnNames;
    },
    onFailedDocumentSettings: (state, { payload }) => {
      state[payload.key].isFetched = true;
      state[payload.key].list = [];
      state[payload.key].columnNames = [];
    },
    switchDocumentSettingLoading: (state, { payload }) => {
      state[payload.key].isLoading =
        payload[payload.key] !== undefined ? payload[payload.key] : !state[payload.key].isLoading;
    },

    onCallDocumentTenantSettings: (state) => {
      state.tenantSettings.isFetched = false;
      state.tenantSettings.data = [];
    },
    onSuccessDocumentTenantSettings: (state, { payload }) => {
      state.tenantSettings.isFetched = true;
      state.tenantSettings.data = payload;
    },
    onFailedDocumentTenantSettings: (state) => {
      state.tenantSettings.isFetched = true;
      state.tenantSettings.data = [];
    },
    switchDocumentTenantSettingLoading: (state) => {
      state.tenantSettings.isLoading = !state.tenantSettings.isLoading;
    },

    onSuccessDocumentSettingAdd: (state, { payload }) => {
      if (payload.data) {
        if (payload.data.Default) {
          state[payload.key].list = [
            ...state[payload.key].list.map((item) => ({
              ...item,
              Default: false
            })),
            payload.data
          ];
        } else {
          state[payload.key].list = [...state[payload.key].list, payload.data];
        }
      }
    },
    onSuccessDocumentSettingEdit: (state, { payload }) => {
      if (payload.data) {
        if (payload.data.Default || payload.data["Default Value"]) {
          state[payload.key].list = state[payload.key].list.map((item) =>
            item[payload.idKey] === payload.data[payload.idKey]
              ? payload.data
              : {
                  ...item,
                  ...(payload.data["Default Value"] ? { "Default Value": false } : { Default: false })
                }
          );
        } else {
          state[payload.key].list = state[payload.key].list.map((item) =>
            item[payload.idKey] === payload.data[payload.idKey] ? payload.data : item
          );
        }
      }
    },
    onSuccessDocumentSettingDelete: (state, { payload }) => {
      if (payload.id !== undefined) {
        state[payload.key].list = state[payload.key].list.filter((item) => item[payload.idKey] !== payload.id);
      }
    },

    // ATTRIBUTES
    onCallAllDocumentAttributes: (state) => {
      state.allDocumentAttributes.isFetched = false;
      state.status.attributeData.list = [];
      state.type.attributeData.list = [];
      state.adjustment_destination.attributeData.list = [];
      state.template.attributeData.list = [];
      state.adjustment_source.attributeData.list = [];
    },
    onSuccessAllDocumentAttributes: (state, { payload }) => {
      state.allDocumentAttributes.isFetched = true;
      Object.keys(payload).forEach((attr) => {
        if (payload[attr].length > 0) {
          const codeFieldName = Object.keys(payload[attr][0]).find((k) => k.includes("_id"));
          const nameFieldName = Object.keys(payload[attr][0]).find((k) => !k.includes("Default") && !k.includes("_id"));
          state[attr].attributeData.list = payload[attr].map((item) => ({
            code: item[codeFieldName],
            name: item[nameFieldName],
            isDefault: item.default_value,
            ...(item.position !== undefined && { position: item.position })
          }));
        } else {
          state[attr].attributeData.list = [];
        }
      });
    },
    onFailedAllDocumentAttributes: (state) => {
      state.status.attributeData.list = [];
      state.allDocumentAttributes.isFetched = true;
      state.type.attributeData.list = [];
      state.adjustment_destination.attributeData.list = [];
      state.template.attributeData.list = [];
      state.adjustment_source.attributeData.list = [];
    },
    onCallDocumentAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = false;
      state[payload.key].attributeData.isLoading = false;
      state[payload.key].attributeData.list = [];
    },
    onSuccessDocumentAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = true;
      if (payload.key === "template" && payload?.data[0]) {
        const codeFieldName = Object.keys(payload.data[0]).find((k) => k.includes("_id"));
        const nameFieldName = Object.keys(payload.data[0]).find((k) => !k.includes("Default") && !k.includes("_id"));
        state[payload.key].attributeData.list = _.orderBy(
          payload.data.map((item) => ({
            code: item[codeFieldName],
            name: item[nameFieldName],
            isDefault: item.default_value,
            ...(item.position !== undefined && { position: item.position })
          })),
          [(list) => list.name.toLowerCase()],
          ["asc"]
        );
      }
      if (payload?.data?.tableData?.[0]) {
        const codeFieldName = Object.keys(payload.data.tableData[0]).find((k) => k.includes("_id"));
        const nameFieldName = Object.keys(payload.data.tableData[0]).find(
          (k) => !k.includes("Default") && !k.includes("_id")
        );

        state[payload.key].attributeData.list = payload.data.tableData.map((item) => ({
          code: item[codeFieldName],
          name: item[nameFieldName],
          isDefault: item.default_value,
          ...(item.position !== undefined && { position: item.position })
        }));
      }
    },
    onFailedDocumentAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = true;
      state[payload.key].attributeData.list = [];
    },
    onSuccessDocumentAttributeAdd: (state, { payload }) => {
      if (payload.data) {
        const codeFieldName = Object.keys(payload.data).find((k) => k.includes("_id"));
        const nameFieldName = Object.keys(payload.data).find((k) => !k.includes("Default") && !k.includes("_id"));

        state[payload.key].attributeData.list = [
          ...current(state[payload.key].attributeData.list),
          {
            code: payload.data[codeFieldName],
            name: payload.data[nameFieldName]
          }
        ];
      }
    },
    switchDocumentAttributeChangeLoading: (state, { payload }) => {
      state[payload.key].attributeData.isLoading =
        payload[payload.key] !== undefined ? payload[payload.key] : !state[payload.key].attributeData.isLoading;
    },
    onSuccessDocumentAttributeChange: (state, { payload }) => {
      if (payload.data && Array.isArray(payload.data)) {
        state.documents = state.documents.map(
          (d) => payload.data.find((newD) => newD.document_id === d.document_id) || d
        );
      }
    },
    onSuccessDocumentActiveStatusChange: (state, { payload }) => {
      const { selected, docNumber } = payload;
      state.documents = state.documents.map((doc) => {
        if (doc["Doc Num"] == docNumber) {
          return { ...doc, Active: doc.document_id === selected };
        }
        return doc;
      });
      state.allDocuments = state.allDocuments.map((doc) => {
        if (doc["Doc Num"] == docNumber) {
          return { ...doc, Active: doc.document_id === selected };
        }
        return doc;
      });
    },
    onSuccessProductServiceTaxableEdit: (state, { payload }) => {
      // console.log("onSuccessProductServiceTaxableEdit warning")
      if (payload.type === "not taxable") {
        const newProductServices = state.document?.productServices?._list.map((pc) => ({ ...pc, taxable: false }));
        state.document.productServices._list = newProductServices;
      }
      if (payload.type === "all taxable") {
        const newProductServices = state.document?.productServices?._list.map((pc) => ({ ...pc, taxable: true }));
        state.document.productServices._list = newProductServices;
      }
      if (payload.type === "part taxable") {
        const newProductServices = state.document?.productServices?._list.map((pc) => {
          if (payload.taxableIDS.includes(pc.category_id)) {
            return { ...pc, taxable: true };
          }
          return { ...pc, taxable: false };
        });
        state.document.productServices._list = newProductServices;
      }
    },
    onSuccessDocumentFromLink: (state, { payload }) => {
      // console.log('onSuccessDocumentFromLink warning');
      const { headData, productServices, itemData, documentItems, tenantData } = payload;
      state.document.data = { head: headData, itemData, documentItems, tenantData };
      const parsedProductServices = productServices.map((dps) => parseDocumentProductService(dps));
      const { newProductServices, total } = calculateDocumentTotalInLink(parsedProductServices);
      state.document.productServices._list = newProductServices;
      const subTotal = total.taxableSubTotal + total.nonTaxableSubTotal;
      // Check this one
      state.documentTotal = {
        ...state.documentTotal,
        taxableSubTotal: total.taxableSubTotal,
        nonTaxableSubTotal: total.nonTaxableSubTotal,
        subTotal
      };

      state._documentTotal = {
        ...state.documentTotal,
        taxableSubTotal: total.taxableSubTotal,
        nonTaxableSubTotal: total.nonTaxableSubTotal,
        subTotal
      };
    },
    onSuccessEditProductServiceInLink: (state, { payload }) => {
      const { newProductServices, total } = calculateDocumentTotalInLink(payload);
      state.document.productServices._list = newProductServices;
      const subTotal = total.taxableSubTotal + total.nonTaxableSubTotal;
      // This one too
      const currentDocumentTotal = cloneDeep(state.documentTotal);
      const currentUnderscoreDocumentTotal = cloneDeep(state._documentTotal);
      state.documentTotal = {
        ...currentDocumentTotal,
        taxableSubTotal: total.taxableSubTotal,
        nonTaxableSubTotal: total.nonTaxableSubTotal,
        subTotal
      };

      state._documentTotal = {
        ...currentUnderscoreDocumentTotal,
        taxableSubTotal: total.taxableSubTotal,
        nonTaxableSubTotal: total.nonTaxableSubTotal,
        subTotal
      };
    },
    onCallSelectedSignatures: (state, { payload }) => {
      state.selectedSignatures = [...state.selectedSignatures, payload];
    },
    removeSignature: (state, { payload }) => {
      const newOptions = state.selectedSignatures.filter((i) => i.itemId !== payload);
      state.selectedSignatures = newOptions;
    },
    resetSignatures: (state) => {
      state.selectedSignatures = [];
    },
    keepSelectedDocumentId: (state, { payload }) => {
      state.selectedDocumentId = payload;
    },
    resetTemplateId: (state) => {
      state.selectedDocumentId = null;
    },
    resetDocumentTotal: (state) => {
      state._documentTotal = _documentTotalConst;
    },
    onSuccessDocumentSend: (state) => {
      // state.document.isReadOnly = true;
      // state.document.head = {
      //   ...state.document.head,
      // read_only: true
      // };
    },
    onCallCopyDocument: (state, { payload }) => {
      state.document.data = {
        head: payload.documentHeadData
      };
      state.document.isCopy = true;
      state.document.isReadOnly = false;
    },
    resetDocumentCopy: (state) => {
      state.document.isCopy = false;
    },

    saveDocumentDraft: (state, { payload }) => {
      state.draftData = payload;
    },
    resetDocumentDraft: (state) => {
      state.draftData = {};
    },
    onUpdateTextElementByKey: (state, { payload }) => {
      // need to update the text element by key, if the key is not found, add it to the array
      const newTextElements = state.document.textElements.map((textElement) =>
        textElement.key === payload.key ? { ...textElement, newValue: payload.newValue } : textElement
      );
      if (!newTextElements.find((textElement) => textElement.key === payload.key)) {
        newTextElements.push({ key: payload.key, newValue: payload.newValue });
      }
      state.document.textElements = newTextElements;
    },
    onResetTextElementByKey: (state, { payload }) => {
      state.document.textElements = state.document.textElements.filter(
        (textElement) => textElement.key !== payload.key
      );
    }
  },
  onResetTextElements: (state) => {
    state.document.textElements = [];
  }
});

export const {
  revertDocumentItemsFromTemplate,
  onDocumentUndoManager,
  onGetExternalIcons,
  onShippingEditCall,
  onShippingEditSuccess,
  onShippingEditFailure,

  onGetContactsSelectSearch,
  onGetContactsSelectSearchSuccess,
  onGetContactsSelectSearchFailure,
  onGetCompaniesSelectSearch,
  onGetCompaniesSelectSearchSuccess,
  onGetCompaniesSelectSearchFailure,

  updateDocumentHead,

  onLoadMergeFields,
  onSuccessMergeFields,
  onFailedMergeFields,
  resetMergeFields,

  keepSelectedDocumentId,
  resetTemplateId,
  resetDocumentTotal,

  onLayoutChange,

  onAddNewNote,

  onCallOutsourceProductService,
  onSuccessOutsourceProductService,
  onFailedOutsourceProductService,

  // calculateAdjustmentsForPreview,

  onCallDocumentProductServicesSearch,
  onSuccessDocumentProductServicesSearch,
  onFailedDocumentProductServicesSearch,
  resetDocumentProductServicesSearch,
  onSuccessDocumentProductServicesSearchPromise,
  onCallDocumentProductServicesSearchPromise,
  onFailedDocumentProductServicesSearchPromise,

  onCallDocumentProductServiceCheckInventory,
  onSuccessDocumentProductServiceCheckInventory,
  onFailedDocumentProductServiceCheckInventory,
  onResetDocumentProductServiceInventory,
  onClearDocumentProductServicesSearch,

  onCallDocuments,
  onSuccessDocuments,
  onSuccessDocumentsTask,
  onFailedDocuments,
  onFailedDocumentsTask,
  resetDocumentTasks,
  switchDocumentsLoading,

  onSuccessDocumentHead,
  onSuccessDocumentHeadWithChangedFields,
  onUpdateDocumentItemFromTemplate,
  onSuccessDocumentItem,
  onFailedDocument,
  resetDocumentHead,
  resetDocumentItem,
  onSuccessDocumentAdd,
  onSuccessDocumentEdit,
  onSuccessDocumentApprove,
  onSuccessDocumentsDelete,
  onSuccessDocumentRequestApproval,

  onCallDocumentNotes,
  onSuccessDocumentNotes,
  onFailedDocumentNotes,

  onCallDocumentAddNote,
  onSuccessDocumentAddNote,
  onFailedDocumentAddNote,
  resetDocumentNotes,

  onCallDocumentTasks,
  onSuccessDocumentTasks,
  onFailedDocumentTasks,

  onCallDocumentRevisions,
  onSuccessDocumentRevisions,
  onFailedDocumentRevisions,

  onSuccessDocumentItemAdd,
  _onSuccessDocumentItemAdd,
  onSuccessDocumentItemEdit,

  // onCallDocumentProductServices,
  // onSuccessDocumentProductServices,
  // onFailedDocumentProductServices,

  switchDocumentProductServiceLoading,
  onSuccessDocumentProductServiceAdd,
  onSuccessDocumentProductServiceEdit,
  onSuccessDocumentProductServicesDelete,

  onCallDocumentSettings,
  onSuccessDocumentSettings,
  onFailedDocumentSettings,

  onCallDocumentTenantSettings,
  onSuccessDocumentTenantSettings,
  onFailedDocumentTenantSettings,
  switchDocumentTenantSettingLoading,

  switchDocumentSettingLoading,
  onSuccessDocumentSettingAdd,
  onSuccessDocumentSettingEdit,
  onSuccessDocumentSettingDelete,

  onFailedAllDocumentAttributes,
  onCallAllDocumentAttributes,
  onSuccessAllDocumentAttributes,

  onCallDocumentAttributes,
  onSuccessDocumentAttributes,
  onFailedDocumentAttributes,
  onSuccessDocumentAttributeAdd,
  switchDocumentAttributeChangeLoading,
  onSuccessDocumentAttributeChange,
  onSuccessDocumentActiveStatusChange,
  onSuccessLocation,
  onSuccessProductServiceTaxableEdit,
  onSuccessEditProductServiceItems,
  onSuccessDocumentFromLink,
  onSuccessEditProductServiceInLink,
  onCallSelectedSignatures,
  removeSignature,
  resetSignatures,
  onSuccessDocumentSend,
  onPdfImport,

  onCallCopyDocument,
  resetDocumentCopy,
  onReceiveLookupNewData,
  onReceiveLookupNewMessage,
  resetLookupData,

  saveDocumentDraft,
  resetDocumentDraft,

  onUpdateTextElementByKey,
  onResetTextElementByKey,
  onResetTextElements
} = documentSettingsSlice.actions;

export default documentSettingsSlice.reducer;
